/**
 * W-001 — Admin login. Stories: US-A01.
 *
 * Separate from the student auth stack: staff sign in with email + password,
 * students sign in with a phone number on mobile. AC2 (a student account is
 * refused) is handled by the auth provider, which signs the session straight
 * back out when there is no active `admin_users` row; here we only render the
 * message it returns.
 */
import { useLogin } from '@refinedev/core'
import { Alert, Button, Card, Form, Input, Layout, Typography } from 'antd'
import { useState } from 'react'
import { ENVIRONMENT } from '@/lib/supabase'
import { useAdminTranslate } from '@/providers/i18n-provider'

interface LoginFormValues {
  email: string
  password: string
}

export const LoginPage = () => {
  const translate = useAdminTranslate()
  const { mutate: login, isPending } = useLogin<LoginFormValues>()
  const [error, setError] = useState<string | null>(null)

  const onFinish = (values: LoginFormValues) => {
    setError(null)
    login(values, {
      onSuccess: data => {
        if (!data.success) {
          setError(data.error?.message ?? translate('admin.login.failed'))
        }
      },
      onError: caught => setError(caught.message),
    })
  }

  return (
    <Layout
      className="admin-login"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card className="admin-login-card">
        <div className="admin-login-brand">
          <img className="admin-brand-mark" src="/logo-mark.svg" alt="" />
          <span className="admin-brand-copy">
            <strong>{translate('common.brand.name')}</strong>
            <span>{translate('admin.login.brandSuffix')}</span>
          </span>
        </div>
        <Typography.Title level={2} className="admin-page-title" style={{ marginTop: 0 }}>
          {translate('admin.login.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {translate('admin.login.subtitle')}
        </Typography.Paragraph>

        {ENVIRONMENT !== 'production' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={translate('admin.login.environment', { environment: ENVIRONMENT })}
          />
        ) : null}

        {error ? (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
        ) : null}

        <Form<LoginFormValues> layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            name="email"
            label={translate('admin.login.emailLabel')}
            rules={[
              { required: true, message: translate('admin.login.emailRequired') },
              { type: 'email', message: translate('admin.login.emailInvalid') },
            ]}
          >
            <Input autoComplete="username" autoFocus />
          </Form.Item>
          <Form.Item
            name="password"
            label={translate('admin.login.passwordLabel')}
            rules={[{ required: true, message: translate('admin.login.passwordRequired') }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={isPending}>
            {translate('admin.login.submit')}
          </Button>
        </Form>
      </Card>
    </Layout>
  )
}
